import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  notificationApiCalls,
  notificationQueryKeys,
} from "../api/notificationApi"
import type { NotificationPreference } from "../types"

/** The settings catalog — static for the session. */
export const useNotificationCatalog = () =>
  useQuery({
    queryKey: notificationQueryKeys.types,
    queryFn: notificationApiCalls.getCatalog,
    staleTime: Infinity,
  })

/** The current user's stored preferences (absence of a row = enabled). */
export const useNotificationPreferences = () =>
  useQuery({
    queryKey: notificationQueryKeys.preferences,
    queryFn: notificationApiCalls.getPreferences,
  })

/**
 * Toggle one (key, channel) preference. Optimistically patches the prefs cache,
 * then on settle refetches the feed + unread count (in-app mutes change them).
 */
export const useUpdatePreference = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: notificationApiCalls.updatePreference,
    onMutate: async (next: NotificationPreference) => {
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.preferences,
      })
      const previous = queryClient.getQueryData<NotificationPreference[]>(
        notificationQueryKeys.preferences,
      )
      queryClient.setQueryData<NotificationPreference[]>(
        notificationQueryKeys.preferences,
        (current = []) => [
          ...current.filter(
            (pref) => !(pref.key === next.key && pref.channel === next.channel),
          ),
          next,
        ],
      )
      return { previous }
    },
    onError: (_error, _next, context) => {
      // Restore whatever was cached before the optimistic write (including
      // `undefined` or `[]`); onSettled refetches regardless.
      queryClient.setQueryData(
        notificationQueryKeys.preferences,
        context?.previous,
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.preferences,
      })
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists })
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.unreadCount,
      })
    },
  })
}
